/* eslint-disable no-nested-ternary */
import { Col, Collapse, Row, Tooltip } from 'antd';
import Avatar from 'antd/lib/avatar/avatar';
import AddressFormatComponent from 'components/common/AddressFormatComponent';
import ButtonWithSpinner from 'components/common/ButtonWithSpinner';
import LinkedDocument from 'components/common/LinkedDocument';
import InputFieldsGenerator from 'components/InputFieldsGenerator';
import { DataFieldProps } from 'components/InputFieldsGenerator/types';
import MapDirection from 'components/MapDirection';
import { FORMAT_DATE_TIME_FOR_DISPLAY } from 'constants/days';
import useRoleCheck from 'hooks/useRoleCheck';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React from 'react';
import ApiEndpoint from 'services/api/apiEndpoint';
import {
  AppointmentNotaryType,
  AppointmentStatus,
  DocumentStatus,
  IAppointment,
  IAppointmentState,
} from 'types/appointments';
// import { appointmentTypeOptionsArray } from 'types/transaction';
import { RoleEnum } from 'types/user';
import { getDefaultAvatarUrl, getNewQueryString } from 'utils';

import { CollapseStyled, IconStyled, InputsContainerStyled, TextOnlyButtonStyled, TextOnlyStyled } from '../../styled';
import { ISignatoryInputProps } from '../../types';

const { Panel } = Collapse;

const AppointmentList: React.FC<{
  data?: Record<string, IAppointmentState>;
  signatoriesData?: Record<string, ISignatoryInputProps>;
  onEditClick?: (appointmentData: IAppointment, id: string) => void;
  onDeleteClick?: (appointmentData: IAppointment, id: string) => void;
  onAddNotaryClick?: (appointmentData: IAppointment) => void;
}> = ({ data, signatoriesData, onEditClick, onDeleteClick, onAddNotaryClick }) => {
  const { isAdmin, isClient, role } = useRoleCheck();
  const generateAppointments = ([id, schedData]: any[], index: number) => {
    const appointmentData: IAppointment = schedData;
    // const type = appointmentTypeOptionsArray.find((sc) => sc.value === appointmentData?.type)?.label || '';
    const signatoryIds = appointmentData?.signatoryIds || [];
    // const { description } = appointmentData;
    const autoGenShippingLabel = appointmentData.autoGenShippingLabel ? 'Yes' : 'No';
    const shippingOptionServiceType = appointmentData.shippingOptions?.serviceType;
    const duration = appointmentData?.duration || 0;
    const address = appointmentData?.address || {};
    const date = moment(appointmentData?.date || '').format(FORMAT_DATE_TIME_FOR_DISPLAY);
    const selectedSignatories = signatoryIds.map(
      (sigId: string) => `${signatoriesData?.[sigId]?.firstName} ${signatoriesData?.[sigId]?.lastName}`,
    );
    const files = appointmentData?.files || [];

    const { notary } = appointmentData;
    const notaryFullName = notary ? `${notary.firstName} ${notary.lastName}` : '';
    const isAppointmentEditable =
      appointmentData.status === AppointmentStatus.OPEN || appointmentData.status === AppointmentStatus.REQUESTED;

    const isAppointmentRequested = appointmentData.status === AppointmentStatus.REQUESTED;
    const notaryFieldValue = isAdmin ? (
      <Row className='gx-m-0'>
        {notary && (
          <Col className='gx-flex-row gx-align-items-center'>
            <Avatar
              src={notary.avatar?.thumbnailPipeFrom || getDefaultAvatarUrl(`${notary?.firstName} ${notary?.lastName}`)}
            />
            <TextOnlyStyled $color='#545454' className='approved-notary-name gx-ml-1'>
              {notaryFullName}
            </TextOnlyStyled>
            {onAddNotaryClick && (
              <TextOnlyButtonStyled
                className='gx-ml-2'
                onClick={() => {
                  onAddNotaryClick(appointmentData);
                }}>
                Change
              </TextOnlyButtonStyled>
            )}
          </Col>
        )}
        {!notary && (
          <>
            {isAppointmentRequested && (
              <Col className='gx-pl-0 gx-mr-2'>
                <TextOnlyStyled $color='#F7CB73'>Requested</TextOnlyStyled>
              </Col>
            )}
            <Col className='gx-p-0'>
              {onAddNotaryClick && (
                <TextOnlyButtonStyled
                  onClick={() => {
                    onAddNotaryClick(appointmentData);
                  }}>
                  {isAppointmentRequested ? 'Detail' : '+ Notary'}
                </TextOnlyButtonStyled>
              )}
            </Col>
          </>
        )}
      </Row>
    ) : (
      <Row className='gx-m-0'>
        {notary && (
          <Row align='middle' className='gx-m-0'>
            <Col flex='32'>
              <Avatar
                src={
                  notary.avatar?.thumbnailPipeFrom || getDefaultAvatarUrl(`${notary?.firstName} ${notary?.lastName}`)
                }
              />
            </Col>
            <Col>
              <TextOnlyStyled className='gx-m-0 gx-pl-1 approved-notary-name' $color='#545454'>
                {notary?.firstName} {notary?.lastName}
              </TextOnlyStyled>
            </Col>
          </Row>
        )}
        {!notary && (
          <Col className='gx-pl-0 gx-mr-2'>
            {isAppointmentRequested ? (
              <TextOnlyStyled $color='#038fde'>Waiting for Notary approval</TextOnlyStyled>
            ) : (
              <TextOnlyStyled $color='#F7CB73'>Awaiting Notary Assignment</TextOnlyStyled>
            )}
          </Col>
        )}
      </Row>
    );

    const documentsDataField: DataFieldProps[] = isAdmin
      ? []
      : [
          {
            label: 'Linked Documents',
            value: (
              <div>
                {(files?.length || 0) > 0
                  ? files.map((file) => (
                      <LinkedDocument
                        key={file.id}
                        id={file.id}
                        name={file.name}
                        description={file.details.description}
                        size={file.size}
                      />
                    ))
                  : 'No linked documents'}
              </div>
            ),
          },
          {
            label: 'Document Shipping Label',
            value: (
              <div>
                {appointmentData.isShippingLabelAutogenerated
                  ? 'Autogenerated Label'
                  : appointmentData.labelFiles?.length
                  ? appointmentData.labelFiles.map((file) => (
                      <LinkedDocument key={file.id} id={file.id} name={file.name} size={file.size} />
                    ))
                  : 'No Shipping Label'}
              </div>
            ),
          },
        ];

    const appointmentDataField: DataFieldProps[] = [
      {
        label: 'Signatories',
        value: selectedSignatories,
        sideFields: [
          {
            label: 'Notary',
            value: notaryFieldValue,
            size: 10,
          },
        ],
      },
      // {
      //   label: 'Description',
      //   value: description,
      // },
      autoGenShippingLabel === 'Yes'
        ? {
            label: 'Shipping Service Type',
            value: shippingOptionServiceType,
            sideFields:
              autoGenShippingLabel === 'Yes'
                ? [{ label: 'Autogenerate Return Shipping Label', value: autoGenShippingLabel, size: 10 }]
                : undefined,
          }
        : {
            label: 'Autogenerate Return Shipping Label',
            value: autoGenShippingLabel,
          },
      {
        label: 'Duration',
        value: `${duration / 3600}h`,
        sideFields: [
          {
            label: 'Date & Time',
            value: date,
            size: 10,
          },
        ],
      },
      {
        label: 'Address',
        value: <AddressFormatComponent addressDetail={address} separateLines />,
      },
      ...documentsDataField,
    ];

    let costFields: DataFieldProps[] = [];
    const isAppointmentInPerson = appointmentData.type === AppointmentNotaryType.IN_PERSON;

    let clientFee: string | number | undefined = isAppointmentInPerson
      ? appointmentData.clientInPersonFee
      : appointmentData.clientRONFee;
    let notaryPay: string | number | undefined = isAppointmentInPerson
      ? appointmentData.notaryGetInPerson
      : appointmentData.notaryGetRON;

    clientFee = clientFee ? `$${clientFee}` : 'Not available';
    notaryPay = notaryPay ? `$${notaryPay}` : 'Not available';

    switch (role) {
      case RoleEnum.ADMIN:
        costFields = [
          {
            label: 'Client Fee',
            value: clientFee,
            sideFields: [
              {
                label: 'Notary Payment',
                value: notaryPay,
                size: 10,
              },
            ],
          },
        ];
        break;
      case RoleEnum.CLIENT:
        costFields = [
          {
            label: 'Client Fee',
            value: clientFee,
          },
        ];
        break;
      case RoleEnum.NOTARY:
        costFields = [
          {
            label: 'Notary Payment',
            value: notaryPay,
          },
        ];
        break;
      default:
        break;
    }
    appointmentDataField.splice(1, 0, ...costFields);

    const shipmentTrackingNumber = appointmentData.labelFiles?.[0]?.details?.trackingNumber;
    const shipmentTrackLink = shipmentTrackingNumber
      ? getNewQueryString(ApiEndpoint.fedExTracking, { tracknumbers: shipmentTrackingNumber })
      : '';

    const isEdit = Boolean(onEditClick && appointmentData.status !== AppointmentStatus.APPROVED && isClient);
    const isDelete = Boolean(onDeleteClick && appointmentData.status !== AppointmentStatus.APPROVED && isClient);
    let appointmentStatusText: any;
    if (isClient)
      switch (appointmentData.documentStatus) {
        case DocumentStatus.SCANNED:
          appointmentStatusText = '(Documents Scanned)';
          break;
        case DocumentStatus.MAILED:
          appointmentStatusText = shipmentTrackLink ? (
            <Tooltip title='To shipment tracking page' placement='top'>
              <a rel='noreferrer' href={shipmentTrackLink} target='_blank'>
                (Documents Mailed)
              </a>
            </Tooltip>
          ) : (
            '(Documents Mailed)'
          );
          break;
        default:
          break;
      }

    return (
      <Panel
        forceRender
        key={id}
        header={
          <div
            id={`${index}`}
            className={`gx-font-weight-medium appointment-panel-header-${index} gx-d-flex gx-align-items-center`}>
            <span>
              Appointment {appointmentData.description} - {date} {appointmentStatusText}
              {/* {type ? `- ${type}` : ''} */}
            </span>
            {isAppointmentEditable && (isEdit || isDelete) && (
              <Col flex='auto' className='gx-text-right'>
                {isEdit && (
                  <ButtonWithSpinner
                    className='gx-mx-0 edit-appointment'
                    bgColor='transparent'
                    bgColorOnHover='transparent'
                    buttonType='default'
                    color='grey'
                    border='none'
                    width='auto'
                    colorOnHover='#038fde'
                    onClick={(e: any) => {
                      e.stopPropagation();
                      onEditClick?.(appointmentData, id);
                    }}>
                    <IconStyled className='icon icon-edit' />
                  </ButtonWithSpinner>
                )}
                {isDelete && (
                  <ButtonWithSpinner
                    bgColor='transparent'
                    bgColorOnHover='transparent'
                    buttonType='default'
                    border='none'
                    width='auto'
                    color='grey'
                    colorOnHover='red'
                    className='delete-appointment'
                    onClick={(e: any) => {
                      e.stopPropagation();
                      onDeleteClick?.(appointmentData, id);
                    }}>
                    <IconStyled className='icon icon-trash' />
                  </ButtonWithSpinner>
                )}
              </Col>
            )}
          </div>
        }>
        <Row className='gx-m-0 gx-w-100'>
          <InputsContainerStyled className='fields-container appointment-detail-container'>
            <InputFieldsGenerator dataFields={appointmentDataField} />
          </InputsContainerStyled>
          <Col style={{ padding: '0 16px', maxWidth: '100%', maxHeight: '500px' }} flex='auto'>
            <Row style={{ height: '100%', minHeight: 300 }}>
              <Col span='24' className='gx-p-0'>
                <MapDirection
                  hideDirectionBox
                  location={address}
                  style={{ width: '100%', height: '100%', marginTop: 10, minHeight: 400 }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Panel>
    );
  };

  return (
    <>{data && !isEmpty(data) && <CollapseStyled>{Object.entries(data).map(generateAppointments)}</CollapseStyled>}</>
  );
};

export default React.memo(AppointmentList);
